<template>
  <div class="card mt-4">
    <div class="card-header">
      <ul class="nav nav-tabs card-header-tabs">
        <li class="nav-item">
          <a class="nav-link" :class="{ active: currentTab === 'units' }" href="#"
            @click.prevent="currentTab = 'units'">Units</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{ active: currentTab === 'products' }" href="#"
            @click.prevent="currentTab = 'products'">Products</a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{ active: currentTab === 'categories' }" href="#"
            @click.prevent="currentTab = 'categories'">Categories</a>
        </li>
      </ul>
    </div>

    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <b-form-select v-model="perPage" :options="pageOptions" class="w-auto" size="sm" title="Rows per page" />
        <b-input-group style="max-width: 300px;">
          <b-form-input v-model="filter" placeholder="Search..." debounce="300" />
          <b-input-group-append>
            <b-button @click="filter = ''" variant="outline-secondary">Clear</b-button>
          </b-input-group-append>
        </b-input-group>
      </div>

      <b-card-group v-if="currentTab === 'units'" class="units-grid" deck>
        <b-card v-for="unit in paginatedUnits" :key="unit.id" class="unit-card" no-body>
          <div class="image-container-wrapper">
            <div v-if="unit.documentUploads && unit.documentUploads.length > 0" class="image-container">
              <img v-for="(doc, index) in unit.documentUploads" :key="doc.id" :src="`${apiBaseUrl}${doc.fileUrl}`"
                alt="Product Image" :class="{ active: unit.activeImageIndex === index }" class="product-image" />
            </div>
            <div v-else class="image-placeholder d-flex align-items-center justify-content-center">
              <i class="bi bi-image-fill text-muted" title="No image available"></i>
            </div>
            <b-badge pill class="status-badge" :variant="unit.assigned ? 'success' : 'warning'">
              {{ unit.assigned ? 'Assigned' : 'Unassigned' }}
            </b-badge>
          </div>

          <b-card-body>
            <h6 class="card-title text-truncate">{{ unit.productName }}</h6>
            <div class="card-text small">
              <div class="mb-1"><b>Unit ID:</b> {{ unit.id }}</div>
              <div><b>Serial:</b> {{ unit.serialNumber || '—' }}</div>
            </div>
          </b-card-body>
          <b-card-footer>
            <b-button size="sm" block variant="primary" @click="viewUnit(unit)">
              View Details
            </b-button>
          </b-card-footer>
        </b-card>
      </b-card-group>

      <b-card-group v-else-if="currentTab === 'products'" class="units-grid" deck>
        <b-card v-for="product in paginatedProducts" :key="product.id" class="unit-card" no-body>
          <div class="image-container-wrapper">
            <div v-if="product.documentUploads && product.documentUploads.length > 0" class="image-container">
              <img v-for="(doc, index) in product.documentUploads" :key="doc.id" :src="`${apiBaseUrl}${doc.fileUrl}`"
                alt="Product Image" :class="{ active: product.activeImageIndex === index }" class="product-image" />
            </div>
            <div v-else class="image-placeholder d-flex align-items-center justify-content-center">
              <i class="bi bi-image-fill text-muted" title="No image available"></i>
            </div>
          </div>

          <b-card-body>
            <h6 class="card-title text-truncate">{{ product.name }}</h6>
            <div class="card-text small">
              <div class="mb-1"><b>Category:</b> {{ product.categoryName }}</div>
              <div><b>Stock:</b> {{ product.stock }}</div>
            </div>
          </b-card-body>
          <b-card-footer>
            <b-button size="sm" block variant="primary" @click="viewProduct(product)">
              View Details
            </b-button>
          </b-card-footer>
        </b-card>
      </b-card-group>

      <b-table v-else-if="currentTab === 'categories'" :items="filteredCategories" :fields="categoryFields"
        :current-page="currentPage" :per-page="perPage" :filter="filter" striped hover small bordered responsive>
        <template #cell(index)="data">
          {{ (currentPage - 1) * perPage + data.index + 1 }}
        </template>
      </b-table>

      <b-pagination v-model="currentPage" :total-rows="activeData.length" :per-page="perPage" align="center"
        class="mt-3" size="sm" />
    </div>

    <UnitDetailsModal v-model="showModal" :unit="selectedUnit" :product="selectedProduct" />
    <ProductDetailsModal v-model="showProductModal" :product="selectedProduct" />
  </div>
</template>

<script>
import { productApi, categoryApi } from "@/api/productApi";
import UnitDetailsModal from '@/components/modals/UnitDetailsModal.vue';
import ProductDetailsModal from '@/components/modals/ProductDetailsModal.vue';

export default {
  components: {
    UnitDetailsModal,
    ProductDetailsModal
  },
  data() {
    return {
      currentTab: 'units',
      units: [],
      products: [],
      categories: [],
      filter: '',
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 20, 50, 100],
      rotationInterval: null,
      apiBaseUrl: " ",

      showModal: false,
      selectedUnit: null,
      selectedProduct: null,
      showProductModal: false, // New state for product modal

      unitFields: [
        { key: "images", label: "Images", tdClass: "text-center", thClass: "text-center" },
        { key: "productName", label: "Product" },
        { key: "id", label: "Unit ID", sortable: true },
        { key: "serialNumber", label: "Serial Number" },
        { key: "assigned", label: "Status" }
      ],
      // This productFields array is no longer used for the products tab, but you can keep it as a backup
      productFields: [
        { key: "index", label: "#" },
        { key: "name", label: "Product Name" },
        { key: "categoryName", label: "Category", sortable: true },
        { key: "stock", label: "Stock" },
        { key: "purchasePrice", label: "Purchase Price" },
        { key: "sellingPrice", label: "Selling Price" }
      ],
      categoryFields: [
        { key: "index", label: "#" },
        { key: "name", label: "Category Name" }
      ]
    };
  },
  computed: {
    formattedProducts() {
      const term = this.filter.toLowerCase();
      return this.products
        .map(p => {
          const category = this.categories.find(c => c.id === p.categoryId);
          return {
            ...p,
            categoryName: category ? category.name : "Uncategorized"
          };
        })
        .filter(p =>
          Object.values(p).some(val =>
            String(val).toLowerCase().includes(term)
          )
        );
    },
    filteredUnits() {
      return this.units
        .map(unit => {
          const product = this.products.find(p => p.id === unit.productId);
          return {
            ...unit,
            productName: product ? product.name : `Product ID ${unit.productId}`
          };
        })
        .filter(item => Object.values(item).some(val =>
          String(val).toLowerCase().includes(this.filter.toLowerCase())
        ));
    },
    filteredCategories() {
      return this.categories.filter(c =>
        Object.values(c).some(val =>
          String(val).toLowerCase().includes(this.filter.toLowerCase())
        )
      );
    },
    activeData() {
      switch (this.currentTab) {
        case 'products': return this.formattedProducts;
        case 'categories': return this.filteredCategories;
        default: return this.filteredUnits;
      }
    },
    paginatedUnits() {
      const startIndex = (this.currentPage - 1) * this.perPage;
      const endIndex = startIndex + this.perPage;
      return this.filteredUnits.slice(startIndex, endIndex);
    },
    paginatedProducts() {
      const startIndex = (this.currentPage - 1) * this.perPage;
      const endIndex = startIndex + this.perPage;
      return this.formattedProducts.slice(startIndex, endIndex);
    }
  },
  methods: {
    startImageRotation() {
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
      }
      this.rotationInterval = setInterval(() => {
        // Apply rotation to both units and products
        const dataToRotate = this.units.concat(this.products);
        dataToRotate.forEach(item => {
          if (item.documentUploads && item.documentUploads.length > 1) {
            const currentIndex = item.activeImageIndex !== undefined ? item.activeImageIndex : -1;
            const nextIndex = (currentIndex + 1) % item.documentUploads.length;
            this.$set(item, 'activeImageIndex', nextIndex);
          }
        });
      }, 3000); // Rotates every 3 seconds
    },
viewUnit(unit) {
  const product = this.products.find(p => p.id === unit.productId);
  
  localStorage.setItem("unitToView", JSON.stringify(unit));
  localStorage.setItem("productForUnit", JSON.stringify(product));

  localStorage.setItem("clickedmenu", "View Unit");
  // this.$router.push("/display");
},
viewProduct(product) {
  localStorage.setItem("productToView", JSON.stringify(product));
  localStorage.setItem("clickedmenu", "View Product");
  // this.$router.push("/display");
  window.location.replace("/display");
}

  },
  created() {
    Promise.all([
      productApi.getAll(),
      productApi.getAllUnits(),
      categoryApi.getAll()
    ])
      .then(([productRes, unitRes, categoryRes]) => {
        // Ensure product data has a documentUploads array for the image carousel to work
        this.products = (productRes.data || []).map(p => ({
          ...p,
          documentUploads: p.documentUploads || []
        }));
        this.units = unitRes.data || [];
        this.categories = categoryRes.data || [];
        this.startImageRotation();
      })
      .catch(err => {
        console.error("Failed to load data:", err);
      });
  },
  beforeDestroy() {
    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
    }
  }
};
</script>

<style scoped>
.units-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}

.unit-card {
  transition: transform 0.2s ease-in-out;
  border-radius: 8px;
  overflow: hidden;
}

.unit-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.image-container-wrapper {
  position: relative;
  width: 100%;
  padding-top: 100%;
  /* 1:1 Aspect Ratio */
  overflow: hidden;
  background-color: #f8f9fa;
}

.image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.product-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.product-image.active {
  opacity: 1;
}

.image-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 3rem;
  color: #dee2e6;
}

.status-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.75rem;
}

.card-title {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.card-text b {
  color: #495057;
}

.card-footer {
  background-color: transparent;
  border-top: none;
  padding: 0.75rem;
}
</style>